import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectAuthData } from "../store/redux/slices/Auth-slice";
import { selectPermissions } from "../store/redux/slices/Permission-slice";
import { resolveFeatureFromPath, PUBLIC_ROUTE } from "../permissions/route-feature-map";
import { ACTIONS } from "../permissions/constants";
import { isAppAdmin as checkAppAdmin } from "../permissions/app-admin";
import useNetworkStatus from "../../hooks/useNetworkStatus";
import { useEffect, useState } from "react";
import { getDataFromDB } from "../../db/dbHelpers";

const PermissionGuard = ({
  requiredFeature,
  requiredAction = ACTIONS.VIEW,
  children,
  isadminDashboard,
  isSetup,
}) => {
  const location = useLocation();
  const authData = useSelector(selectAuthData);
  const permissions = useSelector(selectPermissions);
  const [offlineData, setOfflineData] = useState(null);
  const { isOnline } = useNetworkStatus();

  useEffect(() => {
    if (!isOnline) {
      const fetchOfflineData = async () => {
        const data = await getDataFromDB("credentials");
        setOfflineData(data);
      };
      fetchOfflineData();
    }
  }, [isOnline]);

  const userData = isOnline ? authData?.user : offlineData?.user;
  const role_code = userData ? userData.role_code : null;
  const is_trial = isOnline ? authData?.is_trial : offlineData?.is_trial;
  const trial_type = isOnline
    ? authData?.trial?.trial_type
    : offlineData?.trial?.trial_type;

  const isAppAdmin = checkAppAdmin(role_code);
  const feature = requiredFeature || resolveFeatureFromPath(location.pathname);

  const isPublicRoute = feature === PUBLIC_ROUTE;

  const isAwaitingPermissions =
    isOnline && !isAppAdmin && !isPublicRoute && !permissions;

  let isAllowed;
  if (isAppAdmin || isPublicRoute) {
    isAllowed = true;
  } else if (!feature) {
    isAllowed = false;
  } else {
    isAllowed = !!permissions?.[feature]?.includes(requiredAction);
  }

  let shouldDenyTrialAccess = false;

  if (trial_type === undefined) {
    if (isadminDashboard) {
      shouldDenyTrialAccess = false;
    }
    if (isSetup) {
      shouldDenyTrialAccess = true;
    }
  } else if (trial_type === "unlabeled") {
    if (isadminDashboard) {
      shouldDenyTrialAccess = true;
    }
    if (isSetup) {
      shouldDenyTrialAccess = false;
    }
  } else if (trial_type === "labeled") {
    if (isadminDashboard || isSetup) {
      shouldDenyTrialAccess = true;
    }
  }

  if (isAwaitingPermissions) {
    return null;
  }

  if ((!isAllowed || shouldDenyTrialAccess) && isOnline) {
    return (
      <Navigate to="/access-denied" state={{ from: location.pathname }} replace />
    );
  }

  return <>{children}</>;
};

export default PermissionGuard;
